<script>
import {
  iAlertMessage,
  iAlertMessageType,
  iAlertMessageSize,
  iButton,
  iButtonStyles,
  iButtonTypes,
  iIcon,
  iPagination,
  iSpinner,
} from '@inscribe/signal';
import {
  encodeQueryParams,
  getAndSyncQueryParams,
  updateQueryParams,
} from '@/utils/queryParams';
import DocumentSearchFilter from '@/V2/components/filters/DocumentSearchFilter';
import DatesFilter from '@/V2/components/filters/DatesFilter';
import TagsFilter from '@/V2/components/filters/TagsFilter';
import DocumentTypesFilter from '@/V2/components/filters/DocumentTypesFilter';
import CustomerStatusesFilter from '@/V2/components/filters/CustomerStatusesFilter';
import FileTypesFilter from '@/V2/components/filters/FileTypesFilter';
import RiskLevelsFilter from '@/V2/components/filters/RiskLevelsFilter';
import SortByFilter from '@/V2/components/filters/SortByFilter';
import CustomerFilter from '@/V2/components/filters/CustomerFilter';
import DocumentCard from '@/V2/components/documents/DocumentCard.vue';
import DocumentItemMenu from '@/V2/documents/document-list/DocumentItemMenu';
import { mapActions, mapGetters } from 'vuex';

const ITEMS_PER_PAGE = 15;
const DEFAULT_DOCUMENTS_PARAMS = {
  response_style: 'summary',
};

export default {
  components: {
    DocumentSearchFilter,
    iAlertMessage,
    iButton,
    iIcon,
    iPagination,
    iSpinner,
    DatesFilter,
    TagsFilter,
    DocumentTypesFilter,
    CustomerStatusesFilter,
    FileTypesFilter,
    RiskLevelsFilter,
    SortByFilter,
    CustomerFilter,
    DocumentCard,
    DocumentItemMenu,
  },
  data() {
    return {
      queryParams: getAndSyncQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      }),
      isLoading: false,
      ITEMS_PER_PAGE,
      iButtonStyles,
      iButtonTypes,
      iAlertMessageType,
      iAlertMessageSize,
    };
  },
  computed: {
    ...mapGetters(['getCachedDocuments']),
    combinedQueryParams() {
      return {
        ...DEFAULT_DOCUMENTS_PARAMS,
        ...this.queryParams,
      };
    },
    data() {
      return this.getCachedDocuments(this.combinedQueryParams)?.data;
    },
    metadata() {
      return this.getCachedDocuments(this.combinedQueryParams)?.metadata;
    },
    totalDocuments() {
      return this.metadata?.pagination?.total ?? 0;
    },
    deletedCount() {
      return this.metadata?.deleted_count ?? 0;
    },
    paginationVisible() {
      return this.metadata?.pagination?.total > this.ITEMS_PER_PAGE;
    },
    currentPage() {
      return parseInt(this.queryParams?.page) || 1;
    },
    encodedParams() {
      return encodeQueryParams(this.queryParams);
    },
    returnToPath() {
      return encodeURIComponent(`/documents?${this.encodedParams}`);
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        this.queryParams = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
      },
    },
    queryParams: {
      handler: 'fetchData',
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['fetchAndCacheDocuments']),
    async fetchData() {
      // If there's already a cached value for these params, don't show loading state
      // and fetch updated results in the background
      const cachedValue = this.getCachedDocuments(this.combinedQueryParams);
      if (!cachedValue) {
        this.isLoading = true;
      }
      try {
        await this.fetchAndCacheDocuments(this.combinedQueryParams);
      } catch (e) {
        console.warn(e);
      }
      if (!cachedValue) {
        this.isLoading = false;
      }
    },
    onPageChange(page) {
      if (page !== this.currentPage) {
        updateQueryParams({
          fullPath: this.$route.fullPath,
          router: this.$router,
          store: this.$store,
          updates: {
            page,
            limit: this.ITEMS_PER_PAGE,
          },
        });
      }
    },
    async onDocumentDeleted() {
      // show loading state while we refetch data
      this.isLoading = true;
      try {
        await this.fetchAndCacheDocuments(this.combinedQueryParams);
      } catch (e) {
        console.warn(e);
      }
      this.isLoading = false;
    },
    shouldShowAnalyticsButton() {
      return this.$route.query.return_to === 'analytics';
    },
    viewDocument(document, isRightIconClick) {
      this.$router.push({
        name: 'document-signals',
        params: {
          customer_id: document.customer_id,
          document_id: document.id,
        },
        query: {
          return_to: this.returnToPath,
        },
      });
    },
  },
};
</script>

<template>
  <div class="documents-page__wrapper">
    <div class="documents-page__content">
      <div class="documents-page__header d-flex flex-column i-gap-6 w-100">
        <div class="d-flex flex-row i-gap-6">
          <router-link v-if="shouldShowAnalyticsButton()" to="/analytics">
            <i-button
              data-testid="documents-page-back-button"
              left-icon="medium-arrows-left"
              :type="iButtonTypes.NEUTRAL"
              :button-style="iButtonStyles.GHOST"
              data-dd-action-name="documents-view-back-button"
            />
          </router-link>
          <div class="documents-page__header-title-subtitle">
            <div
              data-testid="documents-page-header-title"
              class="documents-page__header-title"
            >
              Documents
            </div>
            <div class="documents-page__header-subtitle">
              View and manage the documents in your workspace
            </div>
          </div>
        </div>

        <div class="documents-page__search d-flex flex-column">
          <DocumentSearchFilter />
        </div>

        <div class="d-flex flex-row flex-wrap i-gap-3">
          <DatesFilter analytics-label="documents-view-dates-filter" />
          <RiskLevelsFilter />
          <CustomerFilter analytics-label="documents-view-customer-filter" />
          <CustomerStatusesFilter
            entity="documents"
            label="Customer Status"
            analytics-label="documents-view-customer-status-filter"
          />
          <DocumentTypesFilter
            analytics-label="documents-view-document-type-filter"
          />
          <FileTypesFilter analytics-label="documents-view-file-type-filter" />
          <TagsFilter analytics-label="documents-view-tags-filter" />
        </div>
      </div>
      <div
        class="documents-page__sort_and_count d-flex flex-row align-items-space-between"
      >
        <SortByFilter />
        <div
          class="count i-text-neutral-110 i-text-subheading-3 d-flex align-items-center"
        >
          <span v-if="!isLoading">
            {{ totalDocuments }}
            {{ totalDocuments === 1 ? 'document' : 'documents' }}
          </span>
        </div>
      </div>

      <i-alert-message
        v-if="deletedCount > 0"
        class="documents-page__alert"
        :type="iAlertMessageType.NEUTRAL_OUTLINE"
        :size="iAlertMessageSize.SMALL"
        heading="Some documents cannot be displayed because they have been deleted."
        message="These documents are still reflected in the Analytics view."
      ></i-alert-message>

      <div
        v-if="isLoading"
        class="documents-page__loading d-flex flex-row justify-content-center"
      >
        <i-spinner></i-spinner>
      </div>

      <div
        v-if="!isLoading && (!data || !data.length)"
        class="documents-page__empty d-flex flex-column align-items-center justify-content-center i-mt-14"
      >
        <div
          class="documents-page__empty__icon_wrapper d-flex flex-column align-items-center justify-content-center i-mb-8"
        >
          <i-icon
            class="documents-page__empty__icon i-text-primary-110"
            symbol="large-file-text-search"
            :size="46"
          />
        </div>

        <div
          class="documents-page__empty__title i-text-neutral-120 i-text-heading-5 i-mb-4"
        >
          No documents matched the selected filters
        </div>

        <div
          class="documents-page__empty__message i-text-neutral-110 i-text-body-2"
        >
          Matching documents will appear here
        </div>
      </div>

      <!-- documents list -->
      <div
        v-if="!isLoading && data?.length"
        :class="{ 'with-pagination': paginationVisible }"
        class="documents-page__list"
        :key="combinedQueryParams"
      >
        <document-card
          v-for="(docItem, dIndex) in data"
          :key="docItem + dIndex"
          :document="docItem"
          data-testid="document-switcher-item"
          class="document-card"
          :return-to="returnToPath"
          @on-right-icon-click="viewDocument(docItem, true)"
          @on-document-click="viewDocument(docItem, false)"
        >
          <template #right-action>
            <document-item-menu
              :doc="docItem"
              @document-deleted="onDocumentDeleted"
            ></document-item-menu>
          </template>
        </document-card>
      </div>

      <!-- pagination -->
      <div
        v-if="paginationVisible"
        class="documents-page__pagination d-flex flex-row align-items-center justify-content-center i-py-10"
      >
        <i-pagination
          size="md"
          :total-items="totalDocuments"
          :items-per-page="ITEMS_PER_PAGE"
          :selected-page="currentPage"
          @page-click="onPageChange"
        ></i-pagination>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.documents-page {
  &__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding: map.get($increments, 11) map.get($increments, 12);
  }

  &__sort_and_count {
    width: 100%;
    justify-content: space-between;
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: map.get($increments, 10);
    width: 100%;
    max-width: $desktop;
  }

  &__header-title-subtitle {
    display: flex;
    flex-direction: column;
  }

  &__header-title {
    font-size: map.get($text-size-heading, 6);
    font-weight: map.get($text-weights, 'semibold');
  }

  &__header-subtitle {
    font-size: map.get($text-size-standard, 2);
    color: $light-neutral-110;

    @include respond-to('small') {
      display: none;
    }
  }

  &__alert {
    width: 100%;
  }

  &__search {
    margin-top: 28px;
  }

  &__list {
    $borderRadius: map.get($radii, '2xl');

    border-radius: $borderRadius;
    width: 100%;
    border: 1px solid $light-neutral-60;

    .document-card {
      border: 0;
      border-radius: $borderRadius;

      &:not(:last-child) {
        border-bottom: 1px solid $light-neutral-60;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    &.with-pagination {
      margin-bottom: 100px;
    }
  }

  &__pagination {
    position: fixed;
    bottom: 0;
    left: 240px;
    right: 0;
    z-index: 1000;
    background: white;
    border-top: 1px solid $light-neutral-60;

    .pagination {
      width: 1200px;
      margin-bottom: 0;
    }
  }

  &__loading {
    width: 100%;
    min-height: 200px;
  }

  &__empty {
    min-width: 200px;
    width: 100%;

    &__icon_wrapper {
      width: 128px;
      height: 128px;
      border-radius: 100%;
      background: $light-primary-20;
    }
  }
}
</style>
